import { Link } from '@inertiajs/react'
import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import FaqAccordion from '@/components/marketing/FaqAccordion'
import type { FaqItem } from '@/components/marketing/FaqAccordion'
import type { SharedMarketingProps } from '@/types/marketing'

interface ComparisonRow {
  feature: string
  wavbee: string
  competitor: string
}

interface DeepDiveSection {
  title: string
  wavbee_text: string
  competitor_text: string
}

interface MigrationStep {
  step: string
  title: string
  body: string
}

interface PlanRow {
  name: string
  price: string
  royalty: string
  delivery: string
  notes: string
}

interface PlanMatrix {
  as_of: string
  wavbee_plans: PlanRow[]
  competitor_plans: PlanRow[]
  footnote?: string | null
}

interface MatchupPlan {
  name: string
  price: string
  royalty: string
  note: string
}

interface PlanMatchup {
  label: string
  wavbee: MatchupPlan
  competitor: MatchupPlan
  takeaway: string
}

interface ComparePageProps extends SharedMarketingProps {
  competitor: string
  tagline: string
  description: string
  rows: ComparisonRow[]
  sections: DeepDiveSection[]
  faq: FaqItem[]
  migration_steps: MigrationStep[]
  verdict: string
  slug: string
  plan_matrix?: PlanMatrix
  plan_matchups?: PlanMatchup[]
}

/* Full plan table for one provider. Wide content scrolls inside its own
   container so the page never scrolls horizontally. */
function PlanTable({ title, plans, accent }: { title: string; plans: PlanRow[]; accent: boolean }) {
  return (
    <div className="overflow-hidden rounded-[1.75rem] border border-white/8">
      <div className={`px-5 py-4 text-sm font-semibold ${accent ? 'bg-[#ff6a1a]/10 text-[#ff6a1a]' : 'bg-white/4 text-white/64'}`}>
        {title}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead>
            <tr className="border-b border-white/8 bg-white/2 text-xs uppercase tracking-wide text-white/40">
              <th className="px-5 py-3 font-medium">Plan</th>
              <th className="px-5 py-3 font-medium">Price</th>
              <th className="px-5 py-3 font-medium">Royalties</th>
              <th className="px-5 py-3 font-medium">Delivery</th>
              <th className="px-5 py-3 font-medium">What's included</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((plan, i) => (
              <tr key={plan.name} className={`${i < plans.length - 1 ? 'border-b border-white/6' : ''} ${i % 2 === 0 ? 'bg-white/2' : ''}`}>
                <td className="px-5 py-4 font-medium text-white/80">{plan.name}</td>
                <td className="px-5 py-4 text-white/72">{plan.price}</td>
                <td className={`px-5 py-4 ${accent ? 'text-[#ff6a1a]' : 'text-white/72'}`}>{plan.royalty}</td>
                <td className="px-5 py-4 text-white/64">{plan.delivery}</td>
                <td className="px-5 py-4 text-white/56">{plan.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* One side (wavbee or competitor) of a plan-for-plan matchup card. */
function MatchupSide({ plan, accent }: { plan: MatchupPlan; accent: boolean }) {
  return (
    <div className={`rounded-2xl border p-5 ${accent ? 'border-[#ff6a1a]/25 bg-[#ff6a1a]/[0.05]' : 'border-white/8 bg-white/[0.02]'}`}>
      <div className={`text-sm font-semibold ${accent ? 'text-[#ff6a1a]' : 'text-white/64'}`}>{plan.name}</div>
      <div className="mt-1 text-base font-semibold text-white/90">{plan.price}</div>
      <div className={`mt-1 text-xs font-medium ${accent ? 'text-[#ff6a1a]/90' : 'text-white/48'}`}>{plan.royalty}</div>
      <p className="mt-3 text-sm leading-relaxed text-white/56">{plan.note}</p>
    </div>
  )
}

const ALL_COMPETITORS = [
  { name: 'DistroKid', slug: 'distrokid' },
  { name: 'TuneCore', slug: 'tunecore' },
  { name: 'Madverse', slug: 'madverse' },
  { name: 'CD Baby', slug: 'cdbaby' },
  { name: 'Ditto Music', slug: 'ditto' },
  { name: 'RouteNote', slug: 'routenote' },
]

function getOtherComparisons(currentSlug: string) {
  return ALL_COMPETITORS.filter((c) => c.slug !== currentSlug)
}

export default function Compare({
  competitor,
  tagline,
  description,
  rows,
  sections,
  faq,
  migration_steps,
  verdict,
  slug,
  plan_matrix,
  plan_matchups,
}: ComparePageProps) {
  const others = getOtherComparisons(slug)
  const hasMatrix = !!plan_matrix && plan_matrix.competitor_plans.length > 0
  const hasMatchups = !!plan_matchups && plan_matchups.length > 0

  return (
    <>
      <AtmosphericHero minHeight="56vh">
        <div className="mx-auto max-w-3xl text-center space-y-10">
          <ScrollReveal>
            <span className="hero-eyebrow" style={{ justifyContent: 'center' }}>Comparison</span>
          </ScrollReveal>
          <ScrollReveal delay={0.05}>
            <h1 className="hero-headline" style={{ fontSize: 'clamp(2.5rem, 7vw, 5.5rem)' }}>wavbee vs {competitor}</h1>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <p className="hero-body mx-auto">{tagline}</p>
          </ScrollReveal>
          <ScrollReveal delay={0.15}>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-white/64">{description}</p>
          </ScrollReveal>
        </div>
      </AtmosphericHero>

      {/* Plan-for-plan — each wavbee plan paired with its nearest competitor
          tier, so a reader compares equivalent plans instead of a blended row. */}
      {hasMatchups && plan_matchups && (
        <section className="container mx-auto px-6 pt-20">
          <ScrollReveal>
            <div className="mx-auto max-w-4xl">
              <h2 className="mb-2 text-center text-xl font-semibold text-white/90">Plan for plan</h2>
              <p className="mb-8 text-center text-sm text-white/48">
                Each wavbee plan matched against its nearest {competitor} tier.
              </p>
              <div className="space-y-5">
                {plan_matchups.map((matchup, i) => (
                  <ScrollReveal key={matchup.label} delay={i * 0.05}>
                    <div className="rounded-[1.5rem] border border-white/8 bg-white/[0.03] p-5 lg:p-6">
                      <div className="mb-4 text-xs font-semibold uppercase tracking-wide text-white/40">
                        {matchup.label}
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <MatchupSide plan={matchup.wavbee} accent />
                        <MatchupSide plan={matchup.competitor} accent={false} />
                      </div>
                      <p className="mt-4 flex gap-2 text-sm leading-relaxed text-white/72">
                        <span className="text-[#ff6a1a]" aria-hidden="true">→</span>
                        <span>{matchup.takeaway}</span>
                      </p>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </section>
      )}

      {/* Full plan matrix — every plan on both sides, so readers can match
          their own situation instead of comparing two cherry-picked tiers. */}
      {hasMatrix && plan_matrix && (
        <section className="container mx-auto px-6 pt-20">
          <ScrollReveal>
            <div className="mx-auto max-w-5xl">
              <h2 className="mb-2 text-center text-xl font-semibold text-white/90">
                All plans, side by side
              </h2>
              <p className="mb-8 text-center text-xs text-white/40">
                Prices and features verified against {competitor}'s published pricing on {plan_matrix.as_of}.
              </p>
              <div className="space-y-6">
                <PlanTable title="wavbee — all 5 plans" plans={plan_matrix.wavbee_plans} accent />
                <PlanTable title={`${competitor} — all plans`} plans={plan_matrix.competitor_plans} accent={false} />
              </div>
              {plan_matrix.footnote && (
                <p className="mt-4 text-xs leading-relaxed text-white/40">{plan_matrix.footnote}</p>
              )}
            </div>
          </ScrollReveal>
        </section>
      )}

      {/* Quick-glance comparison table */}
      <section className="container mx-auto px-6 py-20">
        <ScrollReveal>
          <div className="mx-auto max-w-4xl">
            <h2 className="mb-8 text-center text-xl font-semibold text-white/90">Quick comparison</h2>
            <div className="overflow-hidden rounded-[1.75rem] border border-white/8">
              {/* Header */}
              <div className="grid grid-cols-3 border-b border-white/8 bg-white/4">
                <div className="px-5 py-4 text-sm font-semibold text-white/48">Feature</div>
                <div className="px-5 py-4 text-center text-sm font-semibold text-[#ff6a1a]">wavbee</div>
                <div className="px-5 py-4 text-center text-sm font-semibold text-white/64">{competitor}</div>
              </div>
              {/* Rows */}
              {rows.map((row, i) => (
                <div
                  key={row.feature}
                  className={`grid grid-cols-3 ${i < rows.length - 1 ? 'border-b border-white/6' : ''} ${i % 2 === 0 ? 'bg-white/2' : ''}`}
                >
                  <div className="px-5 py-4 text-sm font-medium text-white/80">{row.feature}</div>
                  <div className="px-5 py-4 text-center text-sm text-white/72">{row.wavbee}</div>
                  <div className="px-5 py-4 text-center text-sm text-white/56">{row.competitor}</div>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* Deep-dive sections */}
      {sections && sections.length > 0 && (
        <section className="container mx-auto px-6 py-20">
          <div className="mx-auto max-w-4xl">
            <ScrollReveal>
              <h2 className="mb-12 text-center text-xl font-semibold text-white/90">
                Detailed comparison
              </h2>
            </ScrollReveal>
            <div className="space-y-6">
              {sections.map((section, i) => (
                <ScrollReveal key={section.title} delay={i * 0.06}>
                  <div className="rounded-[1.75rem] border border-white/8 bg-white/[0.03] p-6 lg:p-8">
                    <h3 className="mb-6 text-lg font-semibold text-white">{section.title}</h3>
                    <div className="grid gap-6 lg:grid-cols-2">
                      {/* wavbee column */}
                      <div className="rounded-2xl border border-[#ff6a1a]/20 bg-[#ff6a1a]/[0.04] p-5">
                        <div className="mb-3 flex items-center gap-2">
                          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#ff6a1a]/20 text-xs text-[#ff6a1a]">
                            w
                          </span>
                          <span className="text-sm font-semibold text-[#ff6a1a]">wavbee</span>
                        </div>
                        <p className="text-sm leading-relaxed text-white/72">{section.wavbee_text}</p>
                      </div>
                      {/* Competitor column */}
                      <div className="rounded-2xl border border-white/6 bg-white/[0.02] p-5">
                        <div className="mb-3 flex items-center gap-2">
                          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/8 text-xs text-white/48">
                            {competitor[0]}
                          </span>
                          <span className="text-sm font-semibold text-white/48">{competitor}</span>
                        </div>
                        <p className="text-sm leading-relaxed text-white/56">{section.competitor_text}</p>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Migration guide */}
      {migration_steps && migration_steps.length > 0 && (
        <section className="container mx-auto px-6 py-20">
          <div className="mx-auto max-w-3xl">
            <ScrollReveal>
              <h2 className="mb-10 text-center text-xl font-semibold text-white/90">
                How to switch from {competitor} to wavbee
              </h2>
            </ScrollReveal>
            <div className="relative space-y-0">
              {/* Connecting line */}
              <div className="absolute left-6 top-6 bottom-6 w-px bg-gradient-to-b from-[#ff6a1a]/40 via-[#ff6a1a]/20 to-transparent lg:left-7" />

              {migration_steps.map((step, i) => (
                <ScrollReveal key={i} delay={i * 0.08}>
                  <div className="relative flex items-start gap-5 py-4 lg:gap-6">
                    <div className="relative z-10 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border border-[#ff6a1a]/30 bg-[#0a0a0a] text-sm font-bold text-[#ff6a1a] lg:h-14 lg:w-14">
                      {step.step}
                    </div>
                    <div className="pt-2">
                      <h4 className="text-sm font-semibold text-white lg:text-base">{step.title}</h4>
                      <p className="mt-1 text-sm leading-relaxed text-white/64">{step.body}</p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Verdict */}
      {verdict && (
        <section className="container mx-auto px-6 py-20">
          <ScrollReveal>
            <div className="mx-auto max-w-3xl rounded-[1.75rem] border border-white/8 bg-white/[0.03] p-8 lg:p-10">
              <h2 className="mb-4 text-lg font-semibold text-white">The verdict</h2>
              <p className="text-sm leading-relaxed text-white/72 lg:text-base lg:leading-relaxed">{verdict}</p>
            </div>
          </ScrollReveal>
        </section>
      )}

      {/* FAQ */}
      {faq && faq.length > 0 && (
        <section className="marketing-section marketing-section-warm pb-20 pt-16">
          <div className="container mx-auto px-6">
            <ScrollReveal>
              <div className="marketing-section-header">
                <div className="marketing-eyebrow marketing-eyebrow-dark">FAQ</div>
                <h2 className="marketing-section-title marketing-section-title-dark">
                  Frequently asked questions
                </h2>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <div className="mx-auto max-w-3xl">
                <FaqAccordion items={faq} />
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="container mx-auto px-6 pb-16 pt-16">
        <ScrollReveal>
          <div className="marketing-final-cta mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-white">Ready to switch?</h2>
            <p className="mt-3 text-white/64">
              Join wavbee — unlimited releases, real human support, and up to 100% royalties. No hidden fees.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <a href="/apply" className="marketing-button-primary">Apply Now</a>
              <a href="/pricing" className="marketing-button-secondary">See Plans</a>
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* Other comparisons */}
      {others.length > 0 && (
        <section className="container mx-auto px-6 py-24">
          <div className="mx-auto max-w-2xl text-center">
            <h3 className="text-lg font-semibold text-white/72">Other comparisons</h3>
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              {others.map((other) => (
                <Link
                  key={other.slug}
                  href={`/compare/${other.slug}`}
                  className="marketing-button-secondary text-sm"
                  prefetch
                >
                  wavbee vs {other.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  )
}
